package com.speedsoftware.nibbler.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.speedsoftware.nibbler.ui.theme.NibblerColors

/** Small line icons drawn on a canvas (the web canvas has no icon or emoji fonts to rely on). */
enum class PropertyIcon {
    CLOCK, STATUS, PRIORITY, TYPE, PEOPLE, CALENDAR, TAG, COMMENT, CLOSE, CHECK, SEARCH, HISTORY,
    FOLDER, BOARD, DOC, SETTINGS, PLUS, DOTS, FILTER, LOGOUT, SHIELD, ARROW_DOWN, ALERT, MINUS, CHEVRON_DOWN, LIST, HOME, SLIDERS
}

@Composable
fun PropertyIconView(icon: PropertyIcon, modifier: Modifier = Modifier, size: Dp = 16.dp, color: Color = NibblerColors.OnSurfaceVariant) {
    Canvas(modifier = modifier.size(size)) {
        val w = this.size.width
        val h = this.size.height
        val stroke = Stroke(width = w * 0.11f, cap = StrokeCap.Round, join = StrokeJoin.Round)
        when (icon) {
            PropertyIcon.CLOCK, PropertyIcon.HISTORY -> {
                drawCircle(color, radius = w * 0.42f, style = stroke)
                drawLine(color, Offset(w / 2, h * 0.28f), Offset(w / 2, h / 2), strokeWidth = stroke.width, cap = StrokeCap.Round)
                drawLine(color, Offset(w / 2, h / 2), Offset(w * 0.68f, h * 0.62f), strokeWidth = stroke.width, cap = StrokeCap.Round)
            }
            PropertyIcon.STATUS -> {
                drawCircle(color, radius = w * 0.42f, style = stroke)
                drawCircle(color, radius = w * 0.16f)
            }
            PropertyIcon.PRIORITY -> {
                drawLine(color, Offset(w * 0.25f, h * 0.12f), Offset(w * 0.25f, h * 0.9f), strokeWidth = stroke.width, cap = StrokeCap.Round)
                val flag = Path().apply {
                    moveTo(w * 0.25f, h * 0.15f); lineTo(w * 0.82f, h * 0.15f); lineTo(w * 0.62f, h * 0.38f); lineTo(w * 0.82f, h * 0.6f); lineTo(w * 0.25f, h * 0.6f)
                }
                drawPath(flag, color, style = stroke)
            }
            PropertyIcon.TYPE -> {
                drawRoundRect(color, topLeft = Offset(w * 0.12f, h * 0.12f), size = Size(w * 0.76f, h * 0.76f), cornerRadius = CornerRadius(w * 0.14f), style = stroke)
                drawLine(color, Offset(w * 0.32f, h * 0.5f), Offset(w * 0.68f, h * 0.5f), strokeWidth = stroke.width, cap = StrokeCap.Round)
            }
            PropertyIcon.PEOPLE -> {
                drawCircle(color, radius = w * 0.17f, center = Offset(w / 2, h * 0.32f), style = stroke)
                val shoulders = Path().apply { moveTo(w * 0.16f, h * 0.88f); cubicTo(w * 0.16f, h * 0.52f, w * 0.84f, h * 0.52f, w * 0.84f, h * 0.88f) }
                drawPath(shoulders, color, style = stroke)
            }
            PropertyIcon.CALENDAR -> {
                drawRoundRect(color, topLeft = Offset(w * 0.1f, h * 0.18f), size = Size(w * 0.8f, h * 0.7f), cornerRadius = CornerRadius(w * 0.1f), style = stroke)
                drawLine(color, Offset(w * 0.1f, h * 0.4f), Offset(w * 0.9f, h * 0.4f), strokeWidth = stroke.width)
                drawLine(color, Offset(w * 0.32f, h * 0.08f), Offset(w * 0.32f, h * 0.26f), strokeWidth = stroke.width, cap = StrokeCap.Round)
                drawLine(color, Offset(w * 0.68f, h * 0.08f), Offset(w * 0.68f, h * 0.26f), strokeWidth = stroke.width, cap = StrokeCap.Round)
            }
            PropertyIcon.TAG -> {
                val tag = Path().apply {
                    moveTo(w * 0.1f, h * 0.15f); lineTo(w * 0.52f, h * 0.15f); lineTo(w * 0.9f, h * 0.53f); lineTo(w * 0.53f, h * 0.9f); lineTo(w * 0.1f, h * 0.47f); close()
                }
                drawPath(tag, color, style = stroke)
                drawCircle(color, radius = w * 0.06f, center = Offset(w * 0.28f, h * 0.33f))
            }
            PropertyIcon.COMMENT -> {
                val bubble = Path().apply {
                    moveTo(w * 0.14f, h * 0.2f); lineTo(w * 0.86f, h * 0.2f); lineTo(w * 0.86f, h * 0.68f); lineTo(w * 0.5f, h * 0.68f)
                    lineTo(w * 0.3f, h * 0.88f); lineTo(w * 0.3f, h * 0.68f); lineTo(w * 0.14f, h * 0.68f); close()
                }
                drawPath(bubble, color, style = stroke)
            }
            PropertyIcon.CLOSE -> {
                drawLine(color, Offset(w * 0.2f, h * 0.2f), Offset(w * 0.8f, h * 0.8f), strokeWidth = stroke.width * 1.2f, cap = StrokeCap.Round)
                drawLine(color, Offset(w * 0.8f, h * 0.2f), Offset(w * 0.2f, h * 0.8f), strokeWidth = stroke.width * 1.2f, cap = StrokeCap.Round)
            }
            PropertyIcon.CHECK -> {
                val tick = Path().apply { moveTo(w * 0.18f, h * 0.55f); lineTo(w * 0.42f, h * 0.78f); lineTo(w * 0.85f, h * 0.25f) }
                drawPath(tick, color, style = Stroke(width = w * 0.14f, cap = StrokeCap.Round, join = StrokeJoin.Round))
            }
            PropertyIcon.FOLDER -> {
                val folder = Path().apply {
                    moveTo(w * 0.1f, h * 0.28f); lineTo(w * 0.1f, h * 0.8f); lineTo(w * 0.9f, h * 0.8f); lineTo(w * 0.9f, h * 0.32f)
                    lineTo(w * 0.48f, h * 0.32f); lineTo(w * 0.4f, h * 0.2f); lineTo(w * 0.1f, h * 0.2f); close()
                }
                drawPath(folder, color, style = stroke)
            }
            PropertyIcon.BOARD -> {
                drawRoundRect(color, topLeft = Offset(w * 0.1f, h * 0.14f), size = Size(w * 0.8f, h * 0.72f), cornerRadius = CornerRadius(w * 0.1f), style = stroke)
                drawLine(color, Offset(w * 0.37f, h * 0.14f), Offset(w * 0.37f, h * 0.86f), strokeWidth = stroke.width)
                drawLine(color, Offset(w * 0.63f, h * 0.14f), Offset(w * 0.63f, h * 0.86f), strokeWidth = stroke.width)
            }
            PropertyIcon.DOC -> {
                val page = Path().apply {
                    moveTo(w * 0.2f, h * 0.1f); lineTo(w * 0.6f, h * 0.1f); lineTo(w * 0.82f, h * 0.32f); lineTo(w * 0.82f, h * 0.9f); lineTo(w * 0.2f, h * 0.9f); close()
                }
                drawPath(page, color, style = stroke)
                drawLine(color, Offset(w * 0.34f, h * 0.55f), Offset(w * 0.68f, h * 0.55f), strokeWidth = stroke.width, cap = StrokeCap.Round)
                drawLine(color, Offset(w * 0.34f, h * 0.72f), Offset(w * 0.6f, h * 0.72f), strokeWidth = stroke.width, cap = StrokeCap.Round)
            }
            PropertyIcon.SETTINGS -> {
                drawCircle(color, radius = w * 0.17f, style = stroke)
                for (i in 0 until 8) {
                    val angle = i * (kotlin.math.PI / 4).toFloat()
                    val dx = kotlin.math.cos(angle); val dy = kotlin.math.sin(angle)
                    drawLine(color, Offset(w / 2 + dx * w * 0.32f, h / 2 + dy * h * 0.32f), Offset(w / 2 + dx * w * 0.44f, h / 2 + dy * h * 0.44f), strokeWidth = stroke.width * 1.3f, cap = StrokeCap.Round)
                }
                drawCircle(color, radius = w * 0.3f, style = stroke)
            }
            PropertyIcon.PLUS -> {
                drawLine(color, Offset(w / 2, h * 0.2f), Offset(w / 2, h * 0.8f), strokeWidth = stroke.width * 1.2f, cap = StrokeCap.Round)
                drawLine(color, Offset(w * 0.2f, h / 2), Offset(w * 0.8f, h / 2), strokeWidth = stroke.width * 1.2f, cap = StrokeCap.Round)
            }
            PropertyIcon.DOTS -> {
                for (i in 0..2) drawCircle(color, radius = w * 0.075f, center = Offset(w * (0.22f + 0.28f * i), h / 2))
            }
            PropertyIcon.FILTER -> {
                val funnel = Path().apply {
                    moveTo(w * 0.12f, h * 0.2f); lineTo(w * 0.88f, h * 0.2f); lineTo(w * 0.58f, h * 0.55f); lineTo(w * 0.58f, h * 0.82f)
                    lineTo(w * 0.42f, h * 0.72f); lineTo(w * 0.42f, h * 0.55f); close()
                }
                drawPath(funnel, color, style = stroke)
            }
            PropertyIcon.SLIDERS -> {
                listOf(0.25f to 0.65f, 0.5f to 0.3f, 0.75f to 0.55f).forEach { (y, knob) ->
                    drawLine(color, Offset(w * 0.12f, h * y), Offset(w * 0.88f, h * y), strokeWidth = stroke.width, cap = StrokeCap.Round)
                    drawCircle(color, radius = w * 0.09f, center = Offset(w * knob, h * y), style = stroke)
                }
            }
            PropertyIcon.LOGOUT -> {
                val door = Path().apply { moveTo(w * 0.45f, h * 0.15f); lineTo(w * 0.15f, h * 0.15f); lineTo(w * 0.15f, h * 0.85f); lineTo(w * 0.45f, h * 0.85f) }
                drawPath(door, color, style = stroke)
                drawLine(color, Offset(w * 0.38f, h / 2), Offset(w * 0.85f, h / 2), strokeWidth = stroke.width, cap = StrokeCap.Round)
                val head = Path().apply { moveTo(w * 0.68f, h * 0.33f); lineTo(w * 0.85f, h / 2); lineTo(w * 0.68f, h * 0.67f) }
                drawPath(head, color, style = stroke)
            }
            PropertyIcon.SHIELD -> {
                val shield = Path().apply {
                    moveTo(w / 2, h * 0.1f); lineTo(w * 0.84f, h * 0.24f); lineTo(w * 0.84f, h * 0.52f)
                    cubicTo(w * 0.84f, h * 0.74f, w * 0.66f, h * 0.86f, w / 2, h * 0.92f)
                    cubicTo(w * 0.34f, h * 0.86f, w * 0.16f, h * 0.74f, w * 0.16f, h * 0.52f); lineTo(w * 0.16f, h * 0.24f); close()
                }
                drawPath(shield, color, style = stroke)
            }
            PropertyIcon.ARROW_DOWN -> {
                drawLine(color, Offset(w / 2, h * 0.18f), Offset(w / 2, h * 0.82f), strokeWidth = stroke.width, cap = StrokeCap.Round)
                val head = Path().apply { moveTo(w * 0.28f, h * 0.6f); lineTo(w / 2, h * 0.82f); lineTo(w * 0.72f, h * 0.6f) }
                drawPath(head, color, style = stroke)
            }
            PropertyIcon.ALERT -> {
                drawLine(color, Offset(w / 2, h * 0.14f), Offset(w / 2, h * 0.58f), strokeWidth = stroke.width * 1.5f, cap = StrokeCap.Round)
                drawCircle(color, radius = w * 0.08f, center = Offset(w / 2, h * 0.82f))
            }
            PropertyIcon.MINUS -> {
                drawLine(color, Offset(w * 0.2f, h / 2), Offset(w * 0.8f, h / 2), strokeWidth = stroke.width * 1.3f, cap = StrokeCap.Round)
            }
            PropertyIcon.CHEVRON_DOWN -> {
                val chevron = Path().apply { moveTo(w * 0.25f, h * 0.38f); lineTo(w / 2, h * 0.62f); lineTo(w * 0.75f, h * 0.38f) }
                drawPath(chevron, color, style = stroke)
            }
            PropertyIcon.LIST -> {
                for (i in 0..2) {
                    val y = h * (0.25f + 0.25f * i)
                    drawCircle(color, radius = w * 0.05f, center = Offset(w * 0.18f, y))
                    drawLine(color, Offset(w * 0.34f, y), Offset(w * 0.86f, y), strokeWidth = stroke.width, cap = StrokeCap.Round)
                }
            }
            PropertyIcon.HOME -> {
                val house = Path().apply {
                    moveTo(w * 0.12f, h * 0.48f); lineTo(w / 2, h * 0.14f); lineTo(w * 0.88f, h * 0.48f)
                    moveTo(w * 0.24f, h * 0.42f); lineTo(w * 0.24f, h * 0.86f); lineTo(w * 0.76f, h * 0.86f); lineTo(w * 0.76f, h * 0.42f)
                }
                drawPath(house, color, style = stroke)
            }
            PropertyIcon.SEARCH -> {
                drawCircle(color, radius = w * 0.3f, center = Offset(w * 0.43f, h * 0.43f), style = stroke)
                drawLine(color, Offset(w * 0.65f, h * 0.65f), Offset(w * 0.88f, h * 0.88f), strokeWidth = stroke.width, cap = StrokeCap.Round)
            }
        }
    }
}
